import React from 'react'
import { Vector3 } from 'three'
import { getBrickDimensions } from '@utils/brickGeometry'
import type { BrickColor, BrickType } from '@models/brick'
import { BrickStuds } from './BrickStuds'
import { BrickSockets } from './BrickSockets'

interface PreviewBrickProps {
  position: Vector3
  color: BrickColor
  rotation?: number
  isValid?: boolean // Whether this placement position is valid (no collision)
  isSnappedToStud?: boolean // Whether currently snapped to a stud (for visual feedback)
  type?: BrickType // Brick type (default: '1x1')
}

/**
 * PreviewBrick Component
 *
 * Renders a semi-transparent preview of where a brick will be placed.
 * Features:
 * - No physics (ghost brick)
 * - Semi-transparent material (opacity 0.5)
 * - Brighter color to distinguish from placed bricks
 * - No shadow casting
 * - Follows mouse position with grid/stud snapping
 * - Color changes to red when placement is invalid (collision detected)
 * - Visible sockets on bottom for connection visualization
 * - Rotates with R key (Task 3.2.8) - cycles 0° → 90° → 180° → 270° → 0°
 *
 * Visual Feedback:
 * - Valid placement: Normal color with slight glow
 * - Invalid placement: Red color indicates collision
 * - Transparent: Shows this is a preview, not a placed brick
 * - Emissive: Slight glow to make it stand out
 * - Visual rotation: Preview immediately rotates when R key pressed
 *
 * Debug Mode (?debug=true):
 * - Green highlighted studs when snapped to valid connection
 * - Additional debug visuals for development
 *
 * @param position - The snapped grid position to show preview at
 * @param color - The color for the preview brick
 * @param rotation - Rotation in degrees (0, 90, 180, 270) - updated by R key (Task 3.2.8)
 * @param isValid - Whether this position is valid for placement (default: true)
 * @param isSnappedToStud - Whether snapped to stud for vertical stacking
 * @param type - Brick type (default: '1x1')
 */
export const PreviewBrick: React.FC<PreviewBrickProps> = ({
  position,
  color,
  rotation = 0,
  isValid = true,
  isSnappedToStud = false,
  type = '1x1',
}) => {
  // Get actual dimensions based on brick type
  const { width, height, length } = getBrickDimensions(type)
  const depth = length

  // Use red color for invalid placement, otherwise use selected color
  const colorString = isValid
    ? `rgb(${color.r}, ${color.g}, ${color.b})`
    : 'rgb(255, 50, 50)' // Bright red for invalid placement

  return (
    <mesh
      position={[position.x, position.y, position.z]}
      rotation={[0, (rotation * Math.PI) / 180, 0]}
    >
      <boxGeometry args={[width, height, depth]} />
      <meshStandardMaterial
        color={colorString}
        transparent
        opacity={isValid ? 0.5 : 0.3} // More transparent when invalid
        emissive={colorString}
        emissiveIntensity={isValid ? 0.2 : 0.5} // Brighter glow when invalid
        roughness={0.3}
        metalness={0.0}
      />

      {/* Studs on top - highlight green when snapped to valid stud connection */}
      <BrickStuds
        type={type}
        rotation={rotation}
        color={color}
        highlighted={isSnappedToStud && isValid}
      />

      {/* Sockets on bottom - visible on preview to show connection points */}
      <BrickSockets
        type={type}
        rotation={rotation}
        color={color}
        visible={true}
      />
    </mesh>
  )
}
